import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  Avatar,
  Divider,
  Stack
} from '@mui/material';
import {
  Download as DownloadIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { lessonPlanService } from '../services/lessonPlanService';
import type { LessonPlan, LessonPlanFolder } from '../types';
import { FolderSelector } from './FolderSelector';
import { logger } from '../utils/logger';


interface LessonPlanDetailProps {
  open: boolean;
  onClose: () => void;
  lessonPlan: LessonPlan | null;
  onMoveToFolder?: (lessonPlanId: string, folderId: string | null) => void;
  folders?: LessonPlanFolder[];
  canApprove?: boolean;
  onApprove?: () => void;
  onReview?: () => void;
  onReject?: () => void;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Chưa có ngày';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Chưa có ngày';
  }
};

export const LessonPlanDetail: React.FC<LessonPlanDetailProps> = ({
  open,
  onClose,
  lessonPlan,
  onMoveToFolder,
  folders = [],
  canApprove = false,
  onApprove,
  onReview,
  onReject
}) => {
  const [moveFolderDialogOpen, setMoveFolderDialogOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [detailedLessonPlan, setDetailedLessonPlan] = useState<LessonPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && lessonPlan) {
      // Always reload when dialog opens to ensure we have the latest data
      // This is especially important after revise/update operations
      loadLessonPlanDetails();
    } else if (!open) {
      // Clear data when dialog closes
      setDetailedLessonPlan(null);
    }
  }, [open, lessonPlan?.id]); // Use lessonPlan?.id instead of lessonPlan to force reload when id changes

  // Force reload when lessonPlan prop changes (e.g., after revise)
  // This ensures we have the latest data when the prop is updated
  useEffect(() => {
    if (open && lessonPlan) {
      // Reload when lessonPlan prop changes (useful after revise/update)
      loadLessonPlanDetails();
    }
  }, [lessonPlan?.id, lessonPlan?.fileName, lessonPlan?.filePath]); // Reload when key fields change

  const loadLessonPlanDetails = async () => {
    if (!lessonPlan) return;

    setLoading(true);
    setError(null);

    try {
      // Always fetch fresh data from server (no cache)
      const details = await lessonPlanService.getLessonPlanById(lessonPlan.id);
      logger.log('📋 LessonPlanDetail - Loaded details:', details);
      logger.log('📋 LessonPlanDetail - FileName:', details.fileName);
      logger.log('📋 LessonPlanDetail - FilePath:', details.filePath);
      const revHistory = (details as any)?.revisionHistory;
      logger.log('📋 LessonPlanDetail - revisionHistory:', revHistory);
      if (revHistory && Array.isArray(revHistory) && revHistory.length > 0) {
        const lastEntry = revHistory[revHistory.length - 1];
        logger.log('📋 LessonPlanDetail - Last history entry:', lastEntry);
        logger.log('📋 LessonPlanDetail - Last entry action:', lastEntry.action);
        logger.log('📋 LessonPlanDetail - Last entry newFileName:', lastEntry.newFileName);
        logger.log('📋 LessonPlanDetail - Last entry newFilePath:', lastEntry.newFilePath);
        logger.log('📋 LessonPlanDetail - Last entry oldFileName:', lastEntry.oldFileName);
        logger.log('📋 LessonPlanDetail - Last entry oldFilePath:', lastEntry.oldFilePath);
      }
      setDetailedLessonPlan(details);
    } catch (err) {
      logger.error('Error loading lesson plan details:', err);
      setError(err instanceof Error ? err.message : 'Không thể tải chi tiết giáo án');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!detailedLessonPlan) return;

    try {
      const blob = await lessonPlanService.downloadLessonPlan(detailedLessonPlan.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = detailedLessonPlan.fileName || `lesson-plan-${detailedLessonPlan.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      logger.error('Error downloading lesson plan:', err);
      alert('Không thể tải xuống giáo án');
    }
  };

  if (!lessonPlan) return null;

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'submitted':
        return 'Nộp giáo án';
      case 'reviewed':
        return 'Đang xem xét';
      case 'rejected':
        return 'Yêu cầu chỉnh sửa';
      case 'revised':
        return 'Nộp lại';
      case 'approved':
        return 'Đã duyệt';
      default:
        return 'Cập nhật';
    }
  };

  const getActionColor = (action: string): 'primary' | 'success' | 'warning' | 'error' | 'info' | 'grey' => {
    switch (action) {
      case 'submitted':
        return 'primary';
      case 'reviewed':
        return 'info';
      case 'rejected':
        return 'warning';
      case 'revised':
      case 'updated':
        return 'primary';
      case 'approved':
        return 'success';
      case 'draft':
        return 'grey';
      default:
        return 'grey';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'submitted':
        return <SendIcon />;
      case 'reviewed':
        return <VisibilityIcon />;
      case 'rejected':
        return <CloseIcon />;
      case 'revised':
      case 'updated':
        return <EditIcon />;
      case 'approved':
        return <CheckCircleIcon />;
      case 'draft':
        return <EditIcon />;
      default:
        return null;
    }
  };

  const revHistory = (detailedLessonPlan as any)?.revisionHistory;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            {detailedLessonPlan?.title || lessonPlan?.title || 'Chi tiết giáo án'}
          </Typography>
          <Button onClick={onClose} color="inherit">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Đang tải...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && detailedLessonPlan && (
          <Box>
            {/* Basic Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Thông tin giáo án
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Môn học:</strong> {detailedLessonPlan.subject || 'Chưa có'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Tuần:</strong> {detailedLessonPlan.week || 'Chưa có'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Trạng thái:</strong> {detailedLessonPlan.status === 'Pending' ? 'Chờ duyệt' :
                      detailedLessonPlan.status === 'UnderReview' ? 'Đang xem xét' :
                        detailedLessonPlan.status === 'NeedsRevision' ? 'Cần chỉnh sửa' :
                          detailedLessonPlan.status === 'Approved' ? 'Đã duyệt' : detailedLessonPlan.status}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Người nộp:</strong> {detailedLessonPlan.submittedByUser?.fullName || 'Không xác định'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Ngày nộp:</strong> {formatDate(detailedLessonPlan.submissionDate?.toString() || detailedLessonPlan.dateCreated?.toString())}
                  </Typography>
                  {detailedLessonPlan.approvalDate && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Ngày duyệt:</strong> {formatDate(detailedLessonPlan.approvalDate.toString())}
                    </Typography>
                  )}
                  {detailedLessonPlan.approvedByUser && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Người duyệt:</strong> {detailedLessonPlan.approvedByUser.fullName}
                    </Typography>
                  )}
                  {detailedLessonPlan.fileName && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Tên file:</strong> {detailedLessonPlan.fileName}
                    </Typography>
                  )}
                  {detailedLessonPlan.revisionNotes && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Ghi chú chỉnh sửa:</strong> {detailedLessonPlan.revisionNotes}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Digital Content Preview removed as per user request - view in Edit instead */}

            {/* Revision History Timeline */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Lịch sử chỉnh sửa giáo án
                </Typography>
                {(() => {
                  const revHistory = (detailedLessonPlan as any)?.revisionHistory;
                  if (!revHistory || !Array.isArray(revHistory) || revHistory.length === 0) {
                    return (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          Chưa có lịch sử chỉnh sửa
                        </Typography>
                      </Alert>
                    );
                  }

                  return (
                    <Box sx={{ mt: 2 }}>
                      {revHistory.map((history: any, index: number) => {
                        const isLast = index === revHistory.length - 1;

                        return (
                          <Box key={history.id || index} sx={{ position: 'relative', pl: 4, pb: isLast ? 0 : 3 }}>
                            {/* Timeline Line */}
                            {!isLast && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: 15,
                                  top: 40,
                                  bottom: 0,
                                  width: 2,
                                  bgcolor: 'divider'
                                }}
                              />
                            )}

                            {/* Timeline Dot */}
                            <Avatar
                              sx={{
                                position: 'absolute',
                                left: 0,
                                top: 4,
                                width: 32,
                                height: 32,
                                bgcolor: `${getActionColor(history.action)}.main`,
                                color: 'white'
                              }}
                            >
                              {getActionIcon(history.action)}
                            </Avatar>

                            {/* Timeline Content */}
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                <Typography variant="subtitle2" fontWeight="bold" color={`${getActionColor(history.action)}.main`}>
                                  {getActionLabel(history.action)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {history.date ? formatDate(history.date) : 'Chưa có ngày'}
                                </Typography>
                              </Box>

                              {history.notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', mb: 1 }}>
                                  {history.notes}
                                </Typography>
                              )}

                              {/* Show download button for actions with files OR for digital lesson plans (Restored history download for digital plans) */}
                              {((history.action === 'revised' || history.action === 'updated' || history.action === 'submitted' || history.action === 'draft' || history.action === 'reviewed' || history.action === 'approved') &&
                                (history.newFileName || detailedLessonPlan?.is_digital)) && (
                                  <Box sx={{ mt: 1, mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      <strong>{detailedLessonPlan?.is_digital ? 'Nội dung:' : 'File mới:'}</strong>
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      startIcon={<DownloadIcon />}
                                      onClick={async () => {
                                        if (detailedLessonPlan) {
                                          try {
                                            // Check if this is a digital lesson plan
                                            if (detailedLessonPlan.is_digital) {
                                              // Generate PDF for digital plans
                                              const html2pdf = (await import('html2pdf.js')).default;

                                              const element = document.createElement('div');
                                              element.innerHTML = `
                                              <div style="font-family: Arial, sans-serif; padding: 20px;">
                                                <h1 style="text-align: center; color: #333; margin-bottom: 20px;">${detailedLessonPlan.title}</h1>
                                                <div style="margin-bottom: 30px;">
                                                  <p><strong>Giáo viên:</strong> ${detailedLessonPlan.submittedByUser?.fullName || 'N/A'}</p>
                                                  <p><strong>Môn học:</strong> ${detailedLessonPlan.subject || 'N/A'}</p>
                                                  <p><strong>Tuần:</strong> ${detailedLessonPlan.week || 'N/A'}</p>
                                                </div>
                                                <hr style="border: 1px solid #eee; margin-bottom: 30px;" />
                                                <div class="content">
                                                  ${detailedLessonPlan.content || ''}
                                                </div>
                                              </div>
                                            `;

                                              const opt = {
                                                margin: 10,
                                                filename: `${detailedLessonPlan.title || 'lesson-plan'}.pdf`,
                                                image: { type: 'jpeg' as const, quality: 0.98 },
                                                html2canvas: { scale: 2, useCORS: true },
                                                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const }
                                              };

                                              html2pdf().set(opt).from(element).save();
                                            } else {
                                              // Download file for non-digital plans
                                              const blob = await lessonPlanService.downloadLessonPlan(detailedLessonPlan.id);
                                              const url = window.URL.createObjectURL(blob);
                                              const a = document.createElement('a');
                                              a.href = url;
                                              a.download = history.newFileName || 'lesson-plan.pdf';
                                              document.body.appendChild(a);
                                              a.click();
                                              window.URL.revokeObjectURL(url);
                                              document.body.removeChild(a);
                                            }
                                          } catch (err) {
                                            logger.error('Error downloading file:', err);
                                            alert('Không thể tải xuống file');
                                          }
                                        }
                                      }}
                                      sx={{ mr: 1 }}
                                    >
                                      {detailedLessonPlan?.is_digital ? `${detailedLessonPlan.title}.pdf` : history.newFileName}
                                    </Button>
                                  </Box>
                                )}
                              {history.oldFileName && (
                                <Box sx={{ mt: 1, mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    <strong>File cũ:</strong>
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={async () => {
                                      if (history.oldFilePath && detailedLessonPlan) {
                                        try {
                                          const blobUrl = await lessonPlanService.downloadLessonPlanFileWithHeaders(detailedLessonPlan.id, history.oldFilePath);
                                          const a = document.createElement('a');
                                          a.href = blobUrl;
                                          a.download = history.oldFileName || 'lesson-plan.pdf';
                                          document.body.appendChild(a);
                                          a.click();
                                          window.URL.revokeObjectURL(blobUrl);
                                          document.body.removeChild(a);
                                        } catch (err) {
                                          logger.error('Error downloading file:', err);
                                          alert('Không thể tải xuống file');
                                        }
                                      }
                                    }}
                                    sx={{ mr: 1 }}
                                  >
                                    {history.oldFileName}
                                  </Button>
                                </Box>
                              )}

                              {history.user && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                  Người thực hiện: {history.user.fullName || history.user.name || 'Không xác định'}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>

            {/* System Information */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Thông tin hệ thống
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>ID:</strong> {detailedLessonPlan.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Ngày tạo:</strong> {formatDate(detailedLessonPlan.dateCreated?.toString())}
                  </Typography>
                  {detailedLessonPlan.dateModified && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Ngày cập nhật:</strong> {formatDate(detailedLessonPlan.dateModified.toString())}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box >
        )}
      </DialogContent >
      <DialogActions>
        {onMoveToFolder && folders.length > 0 && (
          <Button
            startIcon={<FolderIcon />}
            onClick={() => {
              setSelectedFolderId(detailedLessonPlan?.folderId || null);
              setMoveFolderDialogOpen(true);
            }}
          >
            Di chuyển vào folder
          </Button>

        )}

        {/* Action Buttons for Approver */}
        {/* Action Buttons for Approver */}
        {(() => {
          const currentStatus = detailedLessonPlan?.status || lessonPlan?.status;
          const isPending = currentStatus === 'Pending';
          const isUnderReview = currentStatus === 'UnderReview';

          if (!canApprove) return null;

          return (
            <>
              {isPending && onReview && (
                <Button
                  onClick={onReview}
                  color="info"
                  startIcon={<VisibilityIcon />}
                >
                  Đang xem xét
                </Button>
              )}

              {(isPending || isUnderReview) && (
                <>
                  {onReject && (
                    <Button
                      onClick={onReject}
                      color="warning"
                      startIcon={<CloseIcon />}
                    >
                      Yêu cầu chỉnh sửa
                    </Button>
                  )}
                  {onApprove && (
                    <Button
                      onClick={onApprove}
                      color="success"
                      variant="contained"
                      startIcon={<CheckCircleIcon />}
                    >
                      Duyệt
                    </Button>
                  )}
                </>
              )}
            </>
          );
        })()}

        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>

      {/* Move to Folder Dialog */}
      {
        onMoveToFolder && (
          <Dialog
            open={moveFolderDialogOpen}
            onClose={() => {
              setMoveFolderDialogOpen(false);
              setSelectedFolderId(null);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Di chuyển giáo án vào folder</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <FolderSelector
                  folders={folders}
                  value={selectedFolderId}
                  onChange={setSelectedFolderId}
                  label="Chọn folder"
                  allowNone={true}
                  noneLabel="Không có folder (gỡ khỏi folder)"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setMoveFolderDialogOpen(false);
                  setSelectedFolderId(null);
                }}
              >
                Hủy
              </Button>
              <Button
                onClick={() => {
                  if (detailedLessonPlan) {
                    onMoveToFolder(detailedLessonPlan.id, selectedFolderId);
                    setMoveFolderDialogOpen(false);
                    setSelectedFolderId(null);
                  }
                }}
                variant="contained"
              >
                Di chuyển
              </Button>
            </DialogActions>
          </Dialog>
        )
      }
    </Dialog >
  );
};
